import { ref } from "vue"
import { post } from "@/services/webservice"
import { IS_PRODUCTION } from '@/config'

const queue = ref([])
const isSending = ref(false)

let retryDelay = 1000
const maxRetryDelay = 30000

let flushScheduled = false
let intervalStarted = false

export function useLogger() {

  // -----------------------------
  // ENQUEUE
  // -----------------------------
  const enqueueLog = (level, message, meta = {}) => {
    queue.value.push({
      level,
      message,
      meta: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        ...meta
      }
    })

    // auto-flush trigger
    scheduleFlush()
  }

  // -----------------------------
  // SAFE FLUSH SCHEDULER
  // -----------------------------
  const scheduleFlush = () => {
    if (flushScheduled) return
    flushScheduled = true

    setTimeout(() => {
      flushScheduled = false
      flushQueue()
    }, 0)
  }

  // -----------------------------
  // FLUSH
  // -----------------------------
  const flushQueue = async () => {
    if (isSending.value || queue.value.length === 0) return

    isSending.value = true

    // IMPORTANT: atomic snapshot (prevents race conditions)
    const batch = queue.value.splice(0, queue.value.length)

    try {
      await post("/logs", batch)

      retryDelay = 1000 // reset backoff on success
    } catch (err) {
      // put failed batch back in front of queue
      queue.value.unshift(...batch)

      setTimeout(flushQueue, retryDelay)

      retryDelay = Math.min(retryDelay * 2, maxRetryDelay)
    } finally {
      isSending.value = false
    }
  }

  // -----------------------------
  // OPTIONAL: PERIODIC FLUSH (important safety net)
  // -----------------------------
  const startInterval = () => {
    if (intervalStarted) return
    intervalStarted = true

    setInterval(() => {
      flushQueue()
    }, 5000)
  }

  // -----------------------------
  // DEV LOGGER (console only)
  // -----------------------------
  const sendLog = (level, message, meta = {}) => {
    if(IS_PRODUCTION !== "production"){
      console[level]?.(`[${level.toUpperCase()}] ${message}`, meta)
    }
  }

  // -----------------------------
  // PROD LOGGER (batched)
  // -----------------------------
  const sendLogInProd = (level, message, meta = {}) => {
    if (IS_PRODUCTION !== "production") {
      sendLog(level, message, meta)
      return
    }

    enqueueLog(level, message, meta)
  }

  // -----------------------------
  // CRITICAL ERROR (optional direct send)
  // -----------------------------
  const sendCriticalError = async (error, meta = {}) => {
    const payload = {
      level: "critical",
      message: error?.message || String(error),
      meta: {
        stack: error?.stack,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        ...meta
      }
    }

    try {
      await post("/logs", [payload])
    } catch (e) {
      // fallback: still queue it
      enqueueLog("critical", payload.message, payload.meta)
    }
  }

  return {
    sendLog,
    sendLogInProd,
    sendCriticalError,
    startInterval
  }
}